import { ArrowRight, CheckCircle, Truck } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';

import { GarmentMockup } from '@/components/garment-mockup';
import { ButtonLink, EmptyState } from '@/components/ui';
import { accentTextStyle } from '@/lib/accent';
import { formatCents } from '@/lib/money';
import { tierMultiplier } from '@/lib/pricing/engine';
import { useSuspenseQuery } from '@tanstack/react-query';
import { homeQuery } from '@/queries';

// AC-1.1: the landing renders statically and revalidates hourly (plan §3).

const TIERS = [12, 50, 100, 250];

export default function LandingPage() {
  const { brands, catalogue, methods } = useSuspenseQuery(homeQuery).data;

  const hero = catalogue.slice(0, 3);
  // `Math.min()` of nothing is Infinity, and the landing page rendered
  // "From $∞ including GST" the day the catalogue was empty. R-140.
  const cheapest = catalogue.length
    ? Math.min(...catalogue.map((c) => c.retail_price_cents))
    : null;

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* Hero: asymmetric split. Copy left, the product itself right.      */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto grid max-w-[1180px] items-center gap-12 px-[30px] pb-16 pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:pt-24">
        <div>
          <h1 className="text-[40px] font-extrabold leading-[1.04] tracking-[-0.035em] sm:text-[56px] lg:text-[62px]">
            Custom apparel,
            <br />
            two ways.
          </h1>
          <p className="mt-5 max-w-[46ch] text-lg leading-relaxed text-muted">
            Shop our own-brand labels, or send us your design and we print and ship
            your bulk order.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ButtonLink href="/custom" size="lg">
              Start a custom order
              <ArrowRight size={18} weight="bold" aria-hidden />
            </ButtonLink>
            <ButtonLink href="/shop" weight="secondary" size="lg">
              Browse the store
            </ButtonLink>
          </div>
        </div>

        {/* Real product previews, rendered from the same mockup component the
            studio and the app use. Not a decorative illustration. */}
        <div className="grid grid-cols-2 gap-4">
          {hero[0] && (
            <div className="col-span-2 overflow-hidden rounded-[10px] border border-line bg-panel p-5 shadow-[var(--shadow-panel)]">
              <GarmentMockup
                type={hero[0].type}
                colorHex={hero[0].color_hex}
                designUrl={hero[0].design_url}
                placement={{
                  pos_x: hero[0].pos_x ?? 50,
                  pos_y: hero[0].pos_y ?? 48,
                  scale: hero[0].scale ?? 60,
                }}
                label={`${hero[0].title} in ${hero[0].color_name}`}
                className="mx-auto aspect-square w-full max-w-[340px]"
              />
              <p className="mt-2 text-center text-sm font-semibold">{hero[0].title}</p>
            </div>
          )}
          {hero.slice(1, 3).map((item) => (
            <div
              key={item.id}
              className="overflow-hidden rounded-[10px] border border-line bg-panel p-4"
            >
              <GarmentMockup
                type={item.type}
                colorHex={item.color_hex}
                designUrl={item.design_url}
                placement={{
                  pos_x: item.pos_x ?? 50,
                  pos_y: item.pos_y ?? 48,
                  scale: item.scale ?? 60,
                }}
                label={`${item.title} in ${item.color_name}`}
                className="aspect-square w-full"
              />
            </div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* The two doors. Prototype 1-home, asymmetric so the bulk portal    */}
      {/* (the commercial gap the platform exists to close) leads.          */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] py-6">
        <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
          <article className="reveal relative overflow-hidden rounded-[10px] border border-brand/25 bg-linear-to-br from-brand-wash to-brand-wash-2 p-8 sm:p-10">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-brand-ink">
              Custom &amp; bulk
            </p>
            <h2 className="mt-3 text-[28px] font-bold tracking-[-0.025em] sm:text-[34px]">
              Order your own design
            </h2>
            <p className="mt-3 max-w-[48ch] text-[17px] leading-relaxed text-muted">
              Upload your artwork, preview it live on the garment, pick your decoration,
              and get an instant estimate on large runs.
            </p>
            <ul className="mt-6 grid gap-2.5 text-[15px] sm:grid-cols-2">
              {[
                'Instant ballpark, no phone call',
                'Screen, DTG, heat transfer, embroidery',
                'Volume discounts from 12 units',
                'Artwork proof before we print',
              ].map((point) => (
                <li key={point} className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="mt-0.5 shrink-0 text-brand-ink"
                    aria-hidden
                  />
                  {point}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <ButtonLink href="/custom">
                Start a custom order
                <ArrowRight size={17} weight="bold" aria-hidden />
              </ButtonLink>
            </div>
          </article>

          <article className="reveal flex flex-col rounded-[10px] border border-line bg-panel p-8 sm:p-10">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-muted">
              Retail
            </p>
            <h2 className="mt-3 text-[28px] font-bold tracking-[-0.025em] sm:text-[34px]">
              Shop our brands
            </h2>
            <p className="mt-3 text-[17px] leading-relaxed text-muted">
              Streetwear, fishing wear, caps and hoodies. Printed on demand or held in
              stock, shipped to you.
            </p>
            {/* No catalogue means no price to quote. Saying nothing is
                honest; "From $0" and "From $\u221E" are both worse than
                silence. R-140. */}
            {cheapest !== null && (
              <p className="mt-6 text-[15px] text-muted">
                From <span className="text-2xl font-bold text-ink">{formatCents(cheapest)}</span>{' '}
                including GST
              </p>
            )}
            <div className="mt-auto pt-8">
              <ButtonLink href="/shop" weight="secondary">
                Browse the store
              </ButtonLink>
            </div>
          </article>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Brand tiles, accent rail per brand. Prototype 1-home.             */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] py-14">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-[-0.02em]">Our brands</h2>
          <p className="mt-1 text-[15px] text-muted">Separate labels, one checkout.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {brands.map((brand) => {
            const n = catalogue.filter((c) => c.brand_id === brand.id).length;
            return (
              <Link
                key={brand.id}
                to="/shop/$brand" params={{ brand: brand.slug }}
                className="reveal group flex items-center justify-between gap-3 rounded-[10px] border border-line bg-panel py-6 pl-6 pr-5 transition-shadow hover:shadow-[var(--shadow-panel)]"
                style={{ borderLeft: `5px solid ${brand.accent_hex}` }}
              >
                <span>
                  <span
                    className="accent-text block text-lg font-bold tracking-[-0.02em]"
                    style={accentTextStyle(brand.accent_hex)}
                  >
                    {brand.name}
                  </span>
                  <span className="mt-1 block text-[15px] text-muted">{brand.tagline}</span>
                  <span className="mt-3 block text-sm font-semibold text-brand-ink">
                    {n} {n === 1 ? 'product' : 'products'}
                  </span>
                </span>
                <ArrowRight
                  size={20}
                  weight="bold"
                  className="shrink-0 text-muted transition-transform group-hover:translate-x-1"
                  aria-hidden
                />
              </Link>
            );
          })}
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Decoration methods, bento with mixed cell weights and one         */}
      {/* mockup-backed cell so it is not six text boxes.                   */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] py-14">
        <h2 className="mb-6 max-w-[24ch] text-2xl font-bold tracking-[-0.02em] sm:text-[30px]">
          Four ways to decorate, priced before you ask.
        </h2>
        {/*
          A heading over nothing.

          Every decoration method is switched off while the rate card is
          outstanding (Q-13), so on the live storefront this section rendered
          its promise and then blank space. Seen on the client's real site on
          3 September, during their trade show, on the page a visitor lands on.

          `/custom` and the studio already say the true thing in this situation,
          in these words. Saying it here too costs a visitor one sentence
          instead of a silence they have to interpret. R-366.
        */}
        {methods.length === 0 ? (
          <EmptyState
            title="Decoration prices are not set yet"
            body="Call the studio and we will quote your job by hand while the price list is finished."
          />
        ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {methods.map((m, i) => (
            <article
              key={m.key}
              className={
                'reveal rounded-[10px] border border-line bg-panel p-6 ' +
                (i === 0 ? 'md:col-span-2 md:flex md:items-center md:gap-8' : '')
              }
            >
              {i === 0 && (
                <div className="mb-5 shrink-0 md:mb-0 md:w-44">
                  <div className="checkerboard rounded-[10px] border border-line">
                    <GarmentMockup
                      type="tee"
                      colorHex="#f5f5f5"
                      designUrl="/designs/template1.png"
                      placement={{ pos_x: 50, pos_y: 48, scale: 62 }}
                      label="Screen printed tee sample"
                      className="aspect-square w-full"
                    />
                  </div>
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold tracking-[-0.02em]">{m.name}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted">{m.blurb}</p>
                <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
                  <div className="flex gap-1.5">
                    <dt className="text-muted">Turnaround</dt>
                    <dd className="font-semibold">{m.turnaround}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted">Minimum</dt>
                    <dd className="font-semibold">
                      {m.moq} {m.moq === 1 ? 'unit' : 'units'}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted">Colours</dt>
                    <dd className="font-semibold">
                      {m.max_colours == null ? 'Full colour' : `Up to ${m.max_colours}`}
                    </dd>
                  </div>
                </dl>
                {m.specialty && (
                  <p className="mt-3 text-sm font-semibold text-brand-ink">{m.specialty}</p>
                )}
              </div>
            </article>
          ))}
        </div>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* How a bulk order runs. Process row, no "Step 1" labels.           */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] py-14">
        <div className="rounded-[10px] border border-line bg-panel p-8 sm:p-10">
          <h2 className="max-w-[26ch] text-2xl font-bold tracking-[-0.02em] sm:text-[30px]">
            From artwork to delivery, without a back-and-forth.
          </h2>
          <ol className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                verb: 'Upload',
                body: 'Drop in a PNG, JPG, SVG or WEBP and place it on the garment yourself.',
              },
              {
                verb: 'Price it',
                body: 'The estimate updates as you change method, colours, locations and quantity.',
              },
              {
                verb: 'Approve a proof',
                body: 'We send a numbered proof round. Approve it or ask for a change.',
              },
              {
                verb: 'Track it',
                body: 'Production, quality check and shipping all show on the order timeline.',
              },
            ].map((step, i) => (
              <li key={step.verb} className="reveal">
                <span className="block font-mono text-sm font-bold text-brand-ink">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-2 text-lg font-bold tracking-[-0.02em]">{step.verb}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Volume tiers. Four numbers, not a pricing matrix.                 */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] py-14">
        <div className="rounded-[10px] border border-brand/25 bg-brand-wash-2 p-8 sm:p-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="max-w-[20ch] text-2xl font-bold tracking-[-0.02em] sm:text-[30px]">
                The more you print, the less each print costs.
              </h2>
              <p className="mt-2 max-w-[52ch] text-[15px] text-muted">
                Volume discount applies to the decoration, not the garment, so the saving
                is real and it shows in the estimate before you commit.
              </p>
            </div>
            <ButtonLink href="/custom" weight="secondary">
              Start a custom order
            </ButtonLink>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-brand/20 bg-brand/20 sm:grid-cols-4">
            {TIERS.map((qty) => (
              <div key={qty} className="bg-panel px-5 py-6">
                <p className="font-mono text-3xl font-extrabold tracking-tight text-brand-ink">
                  {Math.round((1 - tierMultiplier(qty)) * 100)}%
                </p>
                <p className="mt-1 text-sm font-semibold">off decoration</p>
                <p className="text-sm text-muted">from {qty} units</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Close.                                                            */}
      {/* ---------------------------------------------------------------- */}
      <section className="mx-auto max-w-[1180px] px-[30px] pb-8 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-6 rounded-[10px] border border-line bg-panel px-8 py-9">
          <div className="flex items-start gap-4">
            <Truck size={26} weight="bold" className="mt-0.5 shrink-0 text-brand-ink" aria-hidden />
            <div>
              <h2 className="text-xl font-bold tracking-[-0.02em]">
                Printed and shipped from Australia
              </h2>
              <p className="mt-1 max-w-[52ch] text-[15px] text-muted">
                Flat-rate shipping, GST-inclusive pricing, and a real proof before anything
                goes on a garment.
              </p>
            </div>
          </div>
          <ButtonLink href="/custom">Start a custom order</ButtonLink>
        </div>
      </section>
    </>
  );
}
